<template>
  <div class="countdown-section">
    <img class="countdown-section__logo" :src="logoImage" alt="" />

    <div class="countdown-section__item">
      <header class="countdown-section__header">
        <span class="countdown-section__header__catch" v-text="headerTextCatch" />
        <span
          v-if="countdownTargetData && countdownTargetData.eventName"
          :class="{
            'countdown-section__header__title--ja': !isEnglish(
              countdownTargetData.eventName[$i18n.locale] || '',
            ),
          }"
          v-text="countdownTargetData.eventName[$i18n.locale]"
        />
      </header>
      <div
        class="countdown-section__content"
        :class="{ 'countdown-section__content--live-mode': hasLiveRace }"
      >
        <div v-if="hasLiveRace" class="countdown-section__play-live">
          <h4
            class="countdown-section__play-live__title"
            v-text="$tc('HomePage.countdown.liveNow')"
          />
          <router-link
            class="countdown-section__play-live__link countdown-section__play-live__link--light--secondary countdown-section__play-live__link--small"
            :class="{ 'countdown-section__play-live__link--circuit-mode': isCircuitMode }"
            :to="liveRaceLink"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 3L14.2854 8.57125C14.6091 8.76546 14.6091 9.23454 14.2854 9.42875L5 15V3Z"
                fill="#00DBA6"
              />
            </svg>
          </router-link>
        </div>

        <div v-else class="countdown-section__countdown">
          <div class="countdown-section__countdown-days">
            <span class="countdown-section__countdown-data" v-text="countdown.days" />
            <span
              class="countdown-section__countdown-unit"
              v-text="$tc('HomePage.countdown.days')"
            />
          </div>
          <div class="countdown-section__countdown__bottom-wrapper">
            <div class="countdown-section__countdown-hours">
              <span class="countdown-section__countdown-data" v-text="countdown.hours" />
              <span
                class="countdown-section__countdown-unit"
                v-text="$tc('HomePage.countdown.hours')"
              />
            </div>
            <div class="countdown-section__countdown-separator"></div>
            <div class="countdown-section__countdown-min">
              <span class="countdown-section__countdown-data" v-text="countdown.min" />
              <span
                class="countdown-section__countdown-unit"
                v-text="$tc('HomePage.countdown.min')"
              />
            </div>
            <div class="countdown-section__countdown-separator"></div>
            <div class="countdown-section__countdown-sec">
              <span class="countdown-section__countdown-data" v-text="countdown.sec" />
              <span
                class="countdown-section__countdown-unit"
                v-text="$tc('HomePage.countdown.sec')"
              />
            </div>
          </div>
        </div>

        <figure
          class="countdown-section__icon"
          :class="{ 'countdown-section__icon-flag': !courseSvgPath }"
        >
          <img :src="courseSvgPath" alt="" />
        </figure>
      </div>

      <circuit-mode-section
        v-if="hasLiveRace"
        :hasDisplayedOnboarding="hasDisplayedOnboarding"
        @onCloseOnboardingScreen="$emit('onCloseOnboardingScreen')"
      />

      <div class="countdown-section__links" v-if="showTicketButton">
        <ticket-link-parts :purchasedTicketStatus="purchasedRelatedEventTicketStatus" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  Ref,
  computed,
  PropType,
} from '@vue/composition-api'
import dayjs from 'dayjs'
import { Multilingual } from '@/store/stores/collectionModule/documents/GeneralTypes'
import {
  CourseType,
  SubjectiveEventType,
} from '@/store/stores/collectionModule/documents/event/CountdownEventDocument'
import Const from '@/util/Const'
import CircuitModeSection from '@/components/HomePage/HomePane/CircuitModeSection.vue'
import StoreUtil from '@/store/StoreUtil'
import TicketLinkParts, {
  PurchasedTicketStatusType,
} from '@/components/HomePage/HomePane/parts/TicketLinkParts.vue'
import usePurchasedTicketData from '@/components/hook/ticket/usePurchasedTicketData'

type CountdownDataType = {
  days: string
  hours: string
  min: string
  sec: string
}
export type UpComingEventDataType = {
  eventId: string | null
  type: SubjectiveEventType | undefined
  starDate: Date
  eventName: Multilingual | undefined
  circuit: CourseType | undefined
  isShowTicketButton: boolean | undefined
}

/**
 * HOME カウントダウンセクション
 */
export default defineComponent({
  name: 'CountdownSection',
  components: {
    TicketLinkParts,
    CircuitModeSection,
  },
  props: {
    /**
     * アプリインストール後、オンボーディング画面を表示したかどうか
     */
    hasDisplayedOnboarding: {
      type: Boolean,
    },
    /**
     * カウントダウン対象データ
     */
    countdownTargetData: {
      type: Object as PropType<UpComingEventDataType>,
    },
    /**
     * ライブ配信中のレースがあるかどうか
     */
    hasLiveRace: {
      type: Boolean,
    },
    /**
     * ライブ配信中の動画再生画面の遷移先
     */
    liveRaceLink: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const appConfigStore = StoreUtil.useStore('AppConfigStore')
    const homePageStore = StoreUtil.useStore('HomePageStore')
    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const { digitalTicketsByYear } = homePageStore
    const { ownOrganization } = mypagePageStore
    const { userPurchasedTicket } = usePurchasedTicketData()

    /** SFgoのロゴ画像 */
    const logoImage = ref('assets/img/HomePage/logo-gray.svg')

    /**
     * チケット購入済みかどうか
     */
    const purchasedRelatedEventTicketStatus: Ref<PurchasedTicketStatusType> = computed(() => {
      const eventStartYear = props.countdownTargetData?.starDate
        ? dayjs(props.countdownTargetData.starDate).year()
        : 0
      // カウンドダウンイベント開始年に一致するチケットイベントデータの中から対象のカウンドダウンイベントに紐づくチケットイベントを取得する
      const ticketEventList = digitalTicketsByYear.value?.[eventStartYear]?.filter(
        (ticketEvent) => ticketEvent.eventId === props.countdownTargetData?.eventId,
      )
      const purchasedTicket = userPurchasedTicket(ticketEventList, ownOrganization.value)
      // カウントダウンイベントに関連するチケットを購入済みの場合、trueを返す
      if (purchasedTicket.productNumbers.availableToday.length > 0) {
        return 'hasAvailableTodayTickets'
      }
      if (purchasedTicket.productNumbers.all.length > 0) {
        return 'hasTickets'
      }
      return 'noTickets'
    })

    /**
     * 右下コース画像取得
     * 画像パス未指定の場合にフラッグアイコンを表示
     */
    const courseSvgPath = computed(() => {
      switch (props.countdownTargetData?.circuit) {
        case 'FUJI_SPEEDWAY':
          return Const.HOME_COUNTDOWN_IMAGE_PATH.FUJI_SPEEDWAY
        case 'AUTOPOLIS':
          return Const.HOME_COUNTDOWN_IMAGE_PATH.AUTOPOLIS
        case 'SPORTSLAND_SUGO':
          return Const.HOME_COUNTDOWN_IMAGE_PATH.SPORTSLAND_SUGO
        case 'SUZUKA_CIRCUIT':
          return Const.HOME_COUNTDOWN_IMAGE_PATH.SUZUKA_CIRCUIT
        case 'TWIN_RING_MOTEGI':
          return Const.HOME_COUNTDOWN_IMAGE_PATH.TWIN_RING_MOTEGI
        default:
          return Const.HOME_COUNTDOWN_IMAGE_PATH.DEFAULT
      }
    })

    const headerTextCatch = computed(() =>
      props.countdownTargetData?.type === 'race' ? 'NEXT RACE' : 'NEXT EVENT',
    )

    const showTicketButton = computed(() => props.countdownTargetData?.isShowTicketButton ?? false)

    const countdown = ref<CountdownDataType>({
      days: '00',
      hours: '00',
      min: '00',
      sec: '00',
    })

    /**
     * 数字穴埋め関数
     * @param num
     * @param length
     * @return string
     */
    const zeroPadding = (num: number, length: number) =>
      String(Array(length).join('0') + num).slice(-length)

    /**
     * カウントダウンタイマー
     */
    let countdownTimer: number
    let remainTime = 0
    const countdownTarget = props.countdownTargetData?.starDate
    const COUNTDOWN_NUMBER_OF_DIGITS = 2

    onMounted(() => {
      // カウントダウンタイマー起動
      countdownTimer = window.setInterval(() => {
        const nowDate = new Date()
        if (!countdownTarget) return
        remainTime = countdownTarget.getTime() - nowDate.getTime() // 差分取得
        remainTime = remainTime < 0 ? 0 : remainTime

        // 3桁の場合にゼロ埋めを3桁にする(デフォルトは2桁)
        const diffDaysLength =
          String(Math.floor(remainTime / 1000 / 60 / 60 / 24)).length > COUNTDOWN_NUMBER_OF_DIGITS
            ? 3
            : COUNTDOWN_NUMBER_OF_DIGITS

        countdown.value = {
          days: zeroPadding(Math.floor(remainTime / 1000 / 60 / 60 / 24), diffDaysLength),
          hours: zeroPadding(
            Math.floor(remainTime / 1000 / 60 / 60) % 24,
            COUNTDOWN_NUMBER_OF_DIGITS,
          ),
          min: zeroPadding(Math.floor(remainTime / 1000 / 60) % 60, COUNTDOWN_NUMBER_OF_DIGITS),
          sec: zeroPadding(Math.floor(remainTime / 1000) % 60, COUNTDOWN_NUMBER_OF_DIGITS),
        }

        // 過去の場合タイマー停止
        if (remainTime === 0) {
          clearInterval(countdownTimer)
        }
      }, 1000)
    })

    onUnmounted(() => {
      // カウントダウンタイマー解除
      if (countdownTimer) {
        clearInterval(countdownTimer)
      }
    })

    /**
     * 半角英数字判定
     * @param str
     * @return boolean
     */
    const isEnglish = (str: string) => str.match(/^[\x20-\x7e]*$/)

    return {
      isCircuitMode: appConfigStore.currentCircuitMode,
      purchasedRelatedEventTicketStatus,
      countdown,
      isEnglish,
      courseSvgPath,
      headerTextCatch,
      logoImage,
      showTicketButton,
    }
  },
})
</script>

<style lang="scss" scoped>
.countdown-section {
  &__item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: min(#{convertPXtoVW(12), 12px}) 0;
    gap: min(#{convertPXtoVW(8), 8px});
    width: calc(100% - min(#{convertPXtoVW(32), 32px}));
    max-width: 600px;
    margin: 0 auto;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 31.77%, rgba(0, 0, 0, 0.045) 100%), #eeeded;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
  }

  &__logo {
    display: none;
    position: absolute;
    left: 15px;
    top: 15px;
  }

  &__header {
    display: flex;
    flex-flow: column nowrap;
    align-content: flex-start;
    align-items: flex-start;
    justify-items: flex-start;
    width: 100%;
    padding: 0 min(#{convertPXtoVW(16), 16px}) min(#{convertPXtoVW(9), 9px});
    border-bottom: 1px solid $black05;
    text-align: left;

    &__catch {
      font-size: min(#{convertPXtoVW(10), 10px});
      font-weight: 600;
      line-height: 1.2;
      text-align: center;
      letter-spacing: 0.08em;
      color: $gray25;
    }

    &__title {
      font-family: $fontHeading;
      font-size: min(#{convertPXtoVW(13), 13px});
      font-weight: 600;
      line-height: 1.23;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: $black;

      &--ja {
        font-family: $fontSignup;
        line-height: 1.54;
      }
    }
  }

  &__content {
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-end;
    justify-content: space-between;
    width: 100%;
    padding: 0 0 0 min(#{convertPXtoVW(16), 16px});

    &--live-mode {
      align-items: center;
    }
  }

  &__countdown {
    width: calc(100% - min(#{convertPXtoVW(118), 118px}));

    &__bottom-wrapper {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      max-width: 210px;
    }

    &-days {
      display: flex;
      flex-flow: row nowrap;
      align-items: flex-start;
      width: 100%;
      margin-bottom: min(#{convertPXtoVW(8), 8px});
      text-align: left;
      gap: min(#{convertPXtoVW(8), 8px});

      .countdown-section__countdown-data {
        font-size: min(#{convertPXtoVW(32), 32px});
      }

      .countdown-section__countdown-unit {
        margin-top: min(#{convertPXtoVW(7), 7px});
      }
    }

    &-hours,
    &-min,
    &-sec {
      display: flex;
      flex-flow: column nowrap;
      align-content: center;
      align-items: center;
      justify-content: center;
      justify-items: center;
      gap: min(#{convertPXtoVW(8), 8px});
      width: calc((100% - 4px - min(#{convertPXtoVW(40), 40px})) / 3);
    }

    &-data {
      font-family: $fontHeading;
      font-style: normal;
      font-weight: 600;
      font-size: min(#{convertPXtoVW(24), 24px});
      line-height: 1;
      letter-spacing: 0.04em;
      color: $black;
    }

    &-unit {
      font-weight: 600;
      font-size: min(#{convertPXtoVW(12), 12px});
      line-height: 1;
      letter-spacing: 0.1em;
      color: $gray25;
      text-transform: uppercase;
    }

    &-separator {
      width: 2px;
      height: min(#{convertPXtoVW(32), 32px});
      background: linear-gradient(
        270deg,
        rgba(255, 255, 255, 0.3) 0%,
        rgba(255, 255, 255, 0.7) 29.69%,
        rgba(255, 255, 255, 0.7) 67.19%,
        rgba(255, 255, 255, 0.3) 100%
      );
      transform: skewY(-45deg);
    }
  }

  &__icon {
    width: min(#{convertPXtoVW(110), 110px});
    padding-right: min(#{convertPXtoVW(10), 10px});

    img {
      width: 100%;
    }

    &-flag {
      width: min(#{convertPXtoVW(80), 80px});
      padding-right: 0;
      margin-right: min(#{convertPXtoVW(12), 12px});
      transform: translateY(min(#{convertPXtoVW(8), 8px}));
    }
  }

  &__play-live {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: calc(100% - min(#{convertPXtoVW(110), 110px}));
    height: 88px;

    &__title {
      color: $gray20;
      text-align: center;
      font-family: $fontHeading;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.23;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    &__link {
      @include button();

      width: 100px;
      height: 36px !important;

      &--circuit-mode {
        svg path {
          fill: $white;
        }
      }
    }
  }

  &__links {
    width: 100%;
    padding: 4px 16px;
  }
}
</style>
